import { Form, Formik } from "formik";

import { forwardRef, useImperativeHandle, useRef } from "react";

import { FaArrowDownLong } from "react-icons/fa6";
import { connectionTypeToCategory } from "../../constants/InvestmentConstant";
import Button from "../../shared/ui/Button";

import cn from "../../lib/cn";
import P from "../../shared/typography/P";
import CustomButton from "../../shared/ui/CustomButton";
import { FormCheckbox } from "../../shared/ui/Form/FormCheckBox";
import FormikLabeledInput from "../../shared/ui/Form/FormikLabeledInput";
import { FormRadioGroup } from "../../shared/ui/Form/FormRadioGroup";
import LabeledFormikSelect from "../../shared/ui/Form/LabeledFormikSelect";
import { generateProposalValidationSchema } from "../../shared/ui/Form/schema";

const planDurationOptions = [
    { label: "5 Years", value: 5 },
    { label: "6 Years", value: 6 },
    { label: "7 Years", value: 7 },
    { label: "8 Years", value: 8 },
    { label: "9 Years", value: 9 },
    { label: "10 Years", value: 10 },
];

const GenerateProposal = forwardRef(
    (
        {
            selectedConnectionType,
            onConnectionTypeChange,
            consumersBill,
            setIsOpen,
            handleConsumerBill,
            generateProposal,
            totalUnits,
            onTotalUnitsChange,
            propertyList,
            checkedState,
            handleCheckboxChange,
            showAdditionalModal,
            onChangeAdditionalModal,
            plan = true,
            subsidy = false,
            showSubsidy = true,
            planDuration = 5,
            onPlanDurationChange,
            isProposalGenerated = false,
            showReAssign = true,
            durationOptions = planDurationOptions,
            handleSubsidyChange = () => null,
        },
        ref
    ) => {
        const formikInnerRef = useRef();
        useImperativeHandle(ref, () => ({
            validate: () => formikInnerRef.current.validateForm(),
            isValid: () => formikInnerRef.current.isValid,
            getErrors: () => formikInnerRef.current.errors,
            submitForm: () => formikInnerRef.current.submitForm(),
            getValues: () => formikInnerRef.current?.values,
        }));

        const initialValues = {
            selectedConnectionType,
            consumersBill,
            totalUnits,
            checkedState,
            planDuration,
            subsidy,
        };

        return (
            <Formik
                innerRef={formikInnerRef}
                enableReinitialize
                initialValues={initialValues}
                validationSchema={generateProposalValidationSchema}
                onSubmit={(values) => generateProposal(values)}
            >
                {({ values, setFieldValue, handleChange }) => (
                    <Form
                        className={cn(
                            "bg-white flex w-full  rounded-lg lg:rounded-2xl flex-col gap-2 lg:gap-3 ",
                            {
                                "lg:gap-2": !isProposalGenerated,
                            }
                        )}
                    >
                        <LabeledFormikSelect
                            label="Connection Type"
                            wrapperClassName="mb-0"
                            name="selectedConnectionType"
                            options={propertyList}
                            icon={<FaArrowDownLong />}
                            value={values.selectedConnectionType}
                            onChange={(value) => {
                                onConnectionTypeChange(value);
                            }}
                        />

                        <FormikLabeledInput
                            label="Consumer’s Bill"
                            name="consumersBill"
                            type="text"
                            wrapperClassName="mb-0"
                            placeholder="Consumer’s Bill"
                            value={
                                values.selectedConnectionType ===
                                    connectionTypeToCategory.RESIDENTIAL_SOCIETY ||
                                values.selectedConnectionType ===
                                    connectionTypeToCategory.INDIVIDUAL_HOMES
                                    ? consumersBill
                                    : values.consumersBill
                            }
                            onChange={(e) => {
                                handleConsumerBill(e);
                            }}
                            disabled={
                                values.selectedConnectionType ===
                                    connectionTypeToCategory.RESIDENTIAL_SOCIETY ||
                                values.selectedConnectionType ===
                                    connectionTypeToCategory.INDIVIDUAL_HOMES
                            }
                            required={true}
                        />

                        <FormikLabeledInput
                            label="Consumer’s Total Unit"
                            name="totalUnits"
                            type="text"
                            wrapperClassName="mb-0"
                            placeholder="Consumer’s Total Unit"
                            value={values.totalUnits}
                            onChange={(e) => {
                                onTotalUnitsChange(e);
                            }}
                            required={true}
                        />
                        {plan && (
                            <LabeledFormikSelect
                                label="Plan Duration"
                                name="planDuration"
                                wrapperClassName="mb-0"
                                options={durationOptions}
                                icon={<FaArrowDownLong />}
                                value={values.planDuration}
                                onChange={(value) => {
                                    setFieldValue("planDuration", value);
                                    onPlanDurationChange(value);
                                }}
                            />
                        )}
                        {showSubsidy &&
                            (values.selectedConnectionType ===
                                "Residential Society" ||
                                values.selectedConnectionType ===
                                    "Individual Homes") && (
                                <FormRadioGroup
                                    name="subsidy"
                                    label="Govt. Subsidy"
                                    onChange={(val) => handleSubsidyChange(val)}
                                    options={[
                                        {
                                            value: true,
                                            label: "Yes",
                                        },
                                        {
                                            value: false,
                                            label: "No",
                                        },
                                    ]}
                                />
                            )}

                        <div className="flex flex-wrap gap-1  justify-between ">
                            <label
                                className="text-secondary font-normal text-sm sm:text-base xl:text-lsm"
                                htmlFor="check"
                            >
                                Proposal Type
                            </label>
                            <div className="flex items-center gap-3.2">
                                {Object.keys(values.checkedState).map((key) => (
                                    <FormCheckbox
                                        key={key}
                                        label={
                                            key.charAt(0).toUpperCase() +
                                            key.slice(1)
                                        }
                                        name={`checkedState.${key}`}
                                        checked={values.checkedState[key]}
                                        onChange={() => {
                                            setFieldValue(
                                                `checkedState.${key}`,
                                                !values.checkedState[key]
                                            );

                                            handleCheckboxChange(key);
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {(values.checkedState.purchase ||
                            values.checkedState.lease) && (
                            <div>
                                <button
                                    type="button"
                                    showAdditionalModal={showAdditionalModal}
                                    onClick={onChangeAdditionalModal}
                                    className="bg-transparent inline-block items-start underline text-blue-100 text-left text-base"
                                >
                                    Add Additional Input
                                </button>
                            </div>
                        )}

                        {generateProposal && (
                            <div className=" mb-[9rem] lg:mb-0">
                                <Button
                                    className="w-full !rounded-1 text-base"
                                    type="submit"
                                    disabled={
                                        Number(values.consumersBill) < 100
                                    }
                                >
                                    Generate Proposal
                                </Button>
                                <div className="flex justify-end lg:hidden">
                                    <P className="text-xs md:text-sm text-primary">
                                        Please Generate Proposal to see details
                                    </P>
                                </div>
                            </div>
                        )}
                        {showReAssign && (
                            <CustomButton
                                className={"hidden lg:flex"}
                                text="Reassign"
                                onClick={() => setIsOpen((prev) => !prev)}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        );
    }
);

export default GenerateProposal;
